/**
 * Browser-source WS client.
 *
 * Connects to `ws://<host>:<port>/browser?key=<k>` with auto-reconnect +
 * heartbeat. All inbound {@link OutMsg} frames are dispatched to
 * `onMessage`; scheduling (reveal-at-ms delays) is handled upstream in the
 * reducer + scenes. Clock sync is bootstrapped from the first
 * `state_snapshot` and refined via ping/pong every 60 s.
 *
 * @packageDocumentation
 */

import type { InMsg, OutMsg } from '@sg/shared';
import { ClockSync } from './clock-sync.js';

/** Possible socket states. Drives reducer's connection-banner slice. */
export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

/** {@link WsClient} options. `onMessage` is required; everything else is defaulted. */
export interface WsClientOpts {
  url: string;
  /** If true, auto-reconnects on close with exponential backoff. Default true. */
  autoReconnect?: boolean;
  maxBackoffMs?: number;
  /** Heartbeat ping interval. Default 15s. */
  heartbeatIntervalMs?: number;
  browserVersion?: string;
  sessionToken?: string;
  /**
   * When true, the client opens the socket but NEVER sends frames — no `hello`
   * handshake, no ping heartbeats, no clock-sync probes. Used for the public
   * `/watch/<handle>/feed` endpoint, which closes (4429) on any inbound frame.
   * Clock-sync still bootstraps opportunistically from `state_snapshot.server_ts`.
   */
  viewer?: boolean;
  onMessage: (m: OutMsg) => void;
  onState?: (s: ConnectionState) => void;
  onSnap?: (prev: number, next: number) => void;
  /** Optional clock injection (for tests). */
  clock?: ClockSync;
}

/**
 * Browser WS client with exponential-backoff reconnect + heartbeat + clock sync.
 * One instance per page; constructed in `main.ts`.
 */
export class WsClient {
  private ws: WebSocket | null = null;
  private readonly opts: Required<Omit<WsClientOpts, 'onState' | 'onSnap' | 'clock' | 'sessionToken' | 'browserVersion' | 'viewer'>> & {
    onState?: (s: ConnectionState) => void;
    sessionToken: string;
    browserVersion: string;
    viewer: boolean;
  };
  readonly clock: ClockSync;
  private state: ConnectionState = 'idle';
  private reconnectAttempt = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(opts: WsClientOpts) {
    this.opts = {
      url: opts.url,
      autoReconnect: opts.autoReconnect ?? true,
      maxBackoffMs: opts.maxBackoffMs ?? 30_000,
      heartbeatIntervalMs: opts.heartbeatIntervalMs ?? 15_000,
      browserVersion: opts.browserVersion ?? '0.1.0',
      sessionToken: opts.sessionToken ?? '',
      viewer: opts.viewer ?? false,
      onMessage: opts.onMessage,
      ...(opts.onState !== undefined && { onState: opts.onState }),
    };
    this.clock =
      opts.clock ??
      new ClockSync(opts.onSnap !== undefined ? { onSnap: opts.onSnap } : {});
  }

  get connectionState(): ConnectionState {
    return this.state;
  }

  connect(): void {
    if (this.destroyed) return;
    this.setState('connecting');
    try {
      this.ws = new WebSocket(this.opts.url);
    } catch {
      this.setState('error');
      this.scheduleReconnect();
      return;
    }
    this.ws.addEventListener('open', this.handleOpen);
    this.ws.addEventListener('message', this.handleMessage);
    this.ws.addEventListener('close', this.handleClose);
    this.ws.addEventListener('error', this.handleError);
  }

  send(msg: InMsg): void {
    if (this.opts.viewer) return; // read-only feed; server closes 4429 on any frame
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(msg));
  }

  close(): void {
    this.destroyed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clock.stopResyncLoop();
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* swallow */
      }
      this.ws = null;
    }
  }

  private handleOpen = (): void => {
    this.reconnectAttempt = 0;
    this.setState('open');
    // Viewer mode: read-only `/watch/<handle>/feed` socket. Server closes
    // (4429) on any inbound frame, so we never send hello/ping/heartbeat.
    // state_snapshot.server_ts will still seed ClockSync via handleMessage().
    if (this.opts.viewer) return;
    this.send({
      type: 'hello',
      session_token: this.opts.sessionToken,
      browser_version: this.opts.browserVersion,
    });
    // Kick off clock sync.
    this.sendPing();
    this.clock.startResyncLoop(() => this.sendPing());
    this.startHeartbeat();
  };

  private handleMessage = (ev: MessageEvent): void => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object' || typeof (parsed as { type?: unknown }).type !== 'string') {
      return;
    }
    const msg = parsed as OutMsg;
    // Clock sync interception: pong + tick + state_snapshot.
    if (msg.type === 'pong') {
      const now = performance.now();
      const pending = this.pendingPing;
      if (pending !== null) {
        this.clock.applyPong(pending, msg.t1, msg.t2, now);
      }
      this.pendingPing = null;
      return;
    }
    if (msg.type === 'state_snapshot') {
      // Opportunistic clock bootstrap from snapshot server_ts.
      if (!this.clock.isInitialized) {
        this.clock.bootstrap(msg.state.server_ts, performance.now());
      }
    }
    if (msg.type === 'tick') {
      // Lightweight re-sync drift check; don't snap on this — proper sync uses ping/pong.
      return;
    }
    this.opts.onMessage(msg);
  };

  private handleClose = (): void => {
    this.stopHeartbeat();
    this.clock.stopResyncLoop();
    this.setState('closed');
    this.ws = null;
    this.scheduleReconnect();
  };

  private handleError = (): void => {
    this.setState('error');
  };

  private pendingPing: number | null = null;

  private sendPing(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.pendingPing = performance.now();
    this.send({ type: 'ping', t0: this.pendingPing });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.sendPing();
    }, this.opts.heartbeatIntervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.destroyed || !this.opts.autoReconnect) return;
    const base = Math.min(500 * 2 ** this.reconnectAttempt, this.opts.maxBackoffMs);
    const jitter = Math.random() * 250;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), base + jitter);
  }

  private setState(next: ConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.opts.onState?.(next);
  }
}
